<template>
  <header
    class="bg-surface-default-low border-static-default-low flex w-full flex-col justify-between gap-16 border-b p-16 md:flex-row md:items-center"
    data-allow-mismatch
  >
    <template v-if="status === 'pending' || status === 'idle'">
      <div class="flex items-center gap-12">
        <RevSkeleton
          :aria-label="i18n(translations.skeletonsLabel)"
          height="48px"
          shape="rectangle"
          width="48px"
        />
        <RevSkeleton
          :aria-label="i18n(translations.skeletonsLabel)"
          height="28px"
          shape="rectangle"
          width="140px"
        />
      </div>
    </template>
    <template v-if="status === 'success' || status === 'error'">
      <div class="flex items-center gap-12">
        <RevIllustration
          v-if="product"
          :alt="product.title"
          class="shrink-0"
          :height="40"
          sizes="40px"
          :src="product.imageUrl"
          :width="48"
        />
        <div v-if="orderline" class="flex flex-col gap-8 md:flex-row">
          <h1 class="heading-3">
            {{ i18n(translations.title, { orderNumber: orderline.orderId }) }}
          </h1>
          <StatusTags :orderline :product-return="mostRecentProductReturn" />
        </div>
      </div>

      <div
        v-if="
          (mostUrgentTask || previousMostUrgentTask) &&
          FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION
        "
        class="flex flex-row items-baseline justify-between gap-12 md:gap-32"
      >
        <RevLink :to="{ name: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS }">
          {{ i18n(translations.openTasksButton) }}
        </RevLink>
        <RevLink @click="handleNextTaskClick">
          {{ i18n(translations.nextTaskButton) }}
        </RevLink>
      </div>
    </template>
  </header>
</template>

<script lang="ts" setup>
import { useAsyncData, useRuntimeConfig } from '#imports'
import { computed, ref, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevSkeleton } from '@ds/components/Skeleton'

import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'
import { ROUTE_NAMES } from '~/constants/routes'
import {
  fetchAndGoToNextTask,
  getMostUrgentTask,
} from '~/scopes/customer-care/components/request.helpers'

import { useOrderlineDetails } from '../../composables/useOrderlineDetails'
import { useOrderlinePublicIdFromRoute } from '../../composables/useOrderlinePublicIdFromRoute'

import translations from './Header.translations'
import StatusTags from './components/StatusTags/StatusTags.vue'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { openErrorToast } = useTheToast()
const { FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION } = useRuntimeConfig().public

const {
  header: { data, status },
} = useOrderlineDetails()

const { orderline, product, mostRecentProductReturn } = data
const orderlinePublicId = useOrderlinePublicIdFromRoute()

const { data: requests, refresh } = useAsyncData(
  `orderline-requests-${orderlinePublicId.value}`,
  () => {
    if (status.value === 'success' && orderlinePublicId.value) {
      return $httpFetch(
        '/seller-after-sales/api/v1/orderlines/:orderlineId/requests',
        {
          pathParams: {
            orderlineId: orderlinePublicId.value,
          },
        },
      )
    }

    return Promise.resolve(null)
  },
  {
    watch: [() => status.value, orderlinePublicId],
  },
)

const previousMostUrgentTask = ref<OrderlineOpenTask | null>(null)
const mostUrgentTask = computed(() => {
  if (requests.value === null) return null

  return getMostUrgentTask(requests.value, null)
})

async function handleNextTaskClick() {
  refresh()
  try {
    fetchAndGoToNextTask(
      mostUrgentTask.value?.id ?? previousMostUrgentTask.value?.id,
    )
  } catch {
    openErrorToast()
  }
}

watch(requests, async () => {
  if (mostUrgentTask.value) {
    previousMostUrgentTask.value = mostUrgentTask.value
  }
})
</script>
